import { useState } from 'react';
import { Badge, Button, Dropdown, Form, Input, Modal, Select, Space, Tag, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  IconKey,
  IconLogout,
  IconUser,
  IconBell,
  IconMessageDots,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '@/api/auth';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/contexts/BrandingContext';
import { ROLE_COLORS, ROLE_LABELS } from '@/lib/role';
import { getNotificationsUnreadCount, submitFeedback } from '@/api/me';

const _PLATFORM_DEFAULT_NAMES = new Set([
  '__default__',
  '锚点科技 · 孵化器一体化运营平台',
]);

interface ChangePasswordFormValues {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

/**
 * Header — 顶栏(品牌名 + 通知 + 用户菜单)。
 *
 * Sprint15:删除 tenant 视角切换 Select(原 Sprint8 Group F 实现)。
 * 理由:严格分工后 admin 不再以租户身份操作业务,跨租户读走聚合仪表盘 + 审计日志,
 * "视角切换"是 impersonate 的无审计弱化版,删 impersonate 同逻辑必须删视角切换。
 * 详见 docs/lessons-learned/sprint15-tenant-switcher.md。
 */
// Sprint48:反馈按钮 + Modal(所有角色可用)
function FeedbackBtn() {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const mu = useMutation({
    mutationFn: submitFeedback,
    onSuccess: (data) => {
      message.success(`反馈已提交,已通知 ${data.notified_count} 位企服/管理员`);
      setOpen(false);
      form.resetFields();
    },
    onError: (err: Error) => message.error(`提交失败:${err.message}`),
  });
  return (
    <>
      <Button type="text" size="small" icon={<IconMessageDots size={16} />}
        onClick={() => setOpen(true)}>反馈</Button>
      <Modal
        title="提交反馈"
        open={open}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        onOk={async () => {
          const v = await form.validateFields();
          mu.mutate(v);
        }}
        confirmLoading={mu.isPending}
        okText="提交" cancelText="取消"
      >
        <Form form={form} layout="vertical" initialValues={{ category: 'suggestion' }}>
          <Form.Item name="category" label="类别" rules={[{ required: true }]}>
            <Select options={[
              { value: 'bug', label: 'Bug 报错' },
              { value: 'suggestion', label: '功能建议' },
              { value: 'question', label: '使用咨询' },
              { value: 'other', label: '其他' },
            ]} />
          </Form.Item>
          <Form.Item name="content" label="详细描述"
            rules={[{ required: true, message: '请描述问题或建议' }, { max: 2000 }]}>
            <Input.TextArea rows={5} placeholder="尽量描述清楚:遇到什么 / 想要什么 / 在哪个页面" />
          </Form.Item>
          <Form.Item name="contact_info" label="联系方式(选)"
            rules={[{ max: 200 }]}>
            <Input placeholder="微信 / 电话 / QQ 等,方便企服联系您" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// Sprint47:铃铛 + 未读 Badge(轮询 60s)
function NotifBell() {
  const q = useQuery({
    queryKey: ['notif', 'unread-count'],
    queryFn: getNotificationsUnreadCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const count = q.data?.count ?? 0;
  return (
    <Badge count={count} size="small" offset={[-2, 2]} overflowCount={99}>
      <IconBell size={18} color="var(--color-text-secondary)" />
    </Badge>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { role, username, logout } = useAuth();
  const { branding } = useBranding();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm] = Form.useForm<ChangePasswordFormValues>();

  // R3-Sprint12-A UX:Header 标题跟 tenant 白标走;无 tenant_branding 时显示通用标题
  // Sprint44-Portal:子门户角色不显示应用名(简化布局,品牌信息够了)
  const isSubportalRole =
    role === 'enterprise' || role === 'mentor' || role === 'investor';
  const hasTenantBranding =
    !_PLATFORM_DEFAULT_NAMES.has(branding.tenant_slug ?? '') &&
    !_PLATFORM_DEFAULT_NAMES.has(branding.company_name ?? '');
  const headerTitle = isSubportalRole
    ? ''
    : hasTenantBranding
      ? branding.company_name ?? '孵化器一体化运营平台'
      : '孵化器一体化运营平台';

  const pwMutation = useMutation({
    mutationFn: (v: ChangePasswordFormValues) =>
      changePassword({ old_password: v.old_password, new_password: v.new_password }),
    onSuccess: (data) => {
      // 新 token 写入(当前会话无感继续);其他设备的旧 token pv 不匹配会 401
      localStorage.setItem('token', data.token);
      message.success('密码已修改;其他设备会被注销');
      setPwOpen(false);
      pwForm.resetFields();
    },
    // onError 走 axios 拦截器全局 toast
  });

  const userMenu: MenuProps = {
    items: [
      {
        key: 'change-password',
        icon: <IconKey size={14} />,
        label: '修改密码',
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <IconLogout size={14} />,
        label: '退出登录',
      },
    ],
    onClick: async ({ key }) => {
      if (key === 'change-password') {
        setPwOpen(true);
      } else if (key === 'logout') {
        await logout();
        navigate('/login');
      }
    },
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 62,
        padding: '0 var(--space-6)',
        background: 'var(--color-bg-card)',
        borderBottom: '1px solid var(--color-border)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <Space size={12}>
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          {headerTitle}
        </div>
      </Space>

      <Space size={16}>
        <FeedbackBtn />
        <NotifBell />
        <Dropdown menu={userMenu} trigger={['click']}>
          <button
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            <IconUser size={16} />
            <span style={{ color: 'var(--color-text-primary)' }}>{username ?? '未登录'}</span>
            {role && <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role]}</Tag>}
          </button>
        </Dropdown>
      </Space>

      {/* R3-Sprint12-B 改密 Modal */}
      <Modal
        title="修改密码"
        open={pwOpen}
        onCancel={() => {
          setPwOpen(false);
          pwForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
      >
        <Form<ChangePasswordFormValues>
          form={pwForm}
          layout="vertical"
          onFinish={(v) => pwMutation.mutate(v)}
          requiredMark={false}
        >
          <Form.Item
            label="当前密码"
            name="old_password"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password autoComplete="current-password" data-testid="pw-old" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            extra="至少 10 字符,需含字母和数字"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 10, message: '至少 10 字符' },
              {
                validator: (_r, v: string) =>
                  !v || (/[A-Za-z]/.test(v) && /[0-9]/.test(v))
                    ? Promise.resolve()
                    : Promise.reject(new Error('需含字母和数字')),
              },
            ]}
          >
            <Input.Password autoComplete="new-password" data-testid="pw-new" />
          </Form.Item>
          <Form.Item
            label="重复新密码"
            name="new_password_confirm"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请重复新密码' },
              ({ getFieldValue }) => ({
                validator: (_r, v: string) =>
                  !v || getFieldValue('new_password') === v
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入不一致')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" data-testid="pw-confirm" />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setPwOpen(false);
                  pwForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={pwMutation.isPending} data-testid="pw-submit">
                修改
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
